import {
  Button,
  InfiniteTable,
  InfiniteTableColumns,
  InfiniteTableSortBy,
  Pill,
  Popover,
  Stack,
  Text,
} from '@sixfold/common-ui';
import { isNil, notNil } from '@sixfold/typed-primitives';
import React, { useCallback, useMemo, useState } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router';

import { CUSTOMER_PRODUCT_NAME } from '../../../constants';
import { FormattedDate } from '../../components/date_formatting/formatted_date';
import { CredentialType, UserPersonaInCompany, UserRoleInCompany } from '../../lib/graphql';
import { beautifyString } from '../../lib/util/string';
import { Routes } from '../../routes';
import { UserInCompany, UserProfile } from '../entities';

export interface UserInCompanyListDataProps {
  data: {
    usersInCompany: UserInCompany[] | null;
  };
}

interface UserInCompanyRow {
  id: string;
  user_id: string;
  name: string;
  email: string;
  role: UserRoleInCompany;
  persona: UserPersonaInCompany | null;
  persona_sort: string;
  credential_types: CredentialType[];
  credential_types_sort: string;
  invitedByUser: UserProfile | null;
  createdAt: string | null;
  lastUiActivityAt: string | null;
  lastUiActivityAt_sort: string;
}

type Props = UserInCompanyListDataProps & {
  company: {
    company_id: string;
  };
};

function prettyProfile(profile: UserProfile | null) {
  if (isNil(profile)) {
    return '';
  }

  const { user_id, first_name, last_name, email } = profile;

  if (notNil(first_name) && notNil(last_name)) {
    return `${first_name} ${last_name}`;
  }

  if (notNil(email)) {
    return email;
  }

  return user_id;
}

const getSortValue = (row: UserInCompanyRow, key: keyof UserInCompanyRow): string | number => {
  switch (key) {
    case 'user_id':
      return parseInt(row.user_id, 10);
    case 'name':
      return row.name;
    case 'email':
      return row.email;
    case 'role':
      return row.role;
    case 'persona_sort':
      return row.persona_sort;
    case 'credential_types_sort':
      return row.credential_types_sort;
    case 'createdAt':
      return row.createdAt ?? '';
    case 'lastUiActivityAt_sort':
      return row.lastUiActivityAt_sort;
    default:
      return '';
  }
};

const sortData = (
  data: UserInCompanyRow[],
  sortBy: InfiniteTableSortBy<keyof UserInCompanyRow>,
): UserInCompanyRow[] => {
  return [...data].sort((a, b) => {
    const aVal = getSortValue(a, sortBy.columnId);
    const bVal = getSortValue(b, sortBy.columnId);
    const cmp =
      typeof aVal === 'number' && typeof bVal === 'number' ? aVal - bVal : String(aVal).localeCompare(String(bVal));
    return sortBy.order === 'ASC' ? cmp : -cmp;
  });
};

const LastActivityContextPopover = () => {
  return (
    <>
      Last active in {CUSTOMER_PRODUCT_NAME} UI
      <Popover placement={'top'} content={<Stack direction="column">Tracking since 28.08.2025</Stack>}>
        <Button iconEnd="small-info-outline" size="xsmall" kind="ghost" hideLabel>
          Help
        </Button>
      </Popover>
    </>
  );
};

export const UserInCompanyList: React.FC<Props> = ({ data, company }) => {
  const navigate = useNavigate();
  const { slug_company_name } = useParams<{ slug_company_name?: string }>();
  const { usersInCompany } = data;

  const [sortBy, setSortBy] = useState<InfiniteTableSortBy<keyof UserInCompanyRow>>({
    columnId: 'user_id',
    order: 'ASC',
  });

  const tableData = useMemo(
    () =>
      sortData(
        (usersInCompany ?? []).map((u) => {
          const credentialTypes = Array.from(new Set((u.user.credentials ?? []).map((credential) => credential.type)));
          credentialTypes.sort();

          return {
            id: u.user.user_id,
            user_id: u.user.user_id,
            name: [u.user.first_name, u.user.last_name].filter((i) => i).join(' '),
            email: u.user.email ?? '-',
            role: u.role,
            persona: u.persona,
            persona_sort: u.persona ?? '-',
            credential_types: credentialTypes,
            credential_types_sort: credentialTypes.join(',') || '-',
            invitedByUser: u.invitedByUser,
            createdAt: u.createdAt,
            lastUiActivityAt: u.lastUiActivityAt,
            lastUiActivityAt_sort: u.lastUiActivityAt ?? new Date(0).toISOString(),
          };
        }),
        sortBy,
      ),
    [usersInCompany, sortBy],
  );

  const columns: InfiniteTableColumns<UserInCompanyRow> = useMemo(
    () => [
      {
        id: 'user_id',
        title: '#',
        width: '5%',
        minWidth: '50px',
        isUserSortable: true,
        align: 'end' as const,
        cellContent: ({ row }) => <>{row.user_id}</>,
      },
      {
        id: 'name',
        title: 'Name',
        width: '14%',
        minWidth: '120px',
        isUserSortable: true,
        cellContent: ({ row }) => <>{row.name}</>,
      },
      {
        id: 'email',
        title: 'Email',
        width: '16%',
        minWidth: '150px',
        isUserSortable: true,
        cellContent: ({ row }) => <>{row.email}</>,
      },
      {
        id: 'role',
        title: 'Role in company',
        width: '10%',
        minWidth: '100px',
        isUserSortable: true,
        cellContent: ({ row }) => (
          <Pill kind={row.role === UserRoleInCompany.ADMIN ? 'info' : 'neutral'}>{beautifyString(row.role)}</Pill>
        ),
      },
      {
        id: 'persona_sort',
        title: 'Persona in company',
        width: '10%',
        minWidth: '110px',
        isUserSortable: true,
        cellContent: ({ row }) =>
          row.persona !== null ? (
            <Pill
              kind={
                row.persona === UserPersonaInCompany.CUSTOMER_SUPPORT
                  ? 'info'
                  : row.persona === UserPersonaInCompany.TRANSPORT_PLANNER ||
                      row.persona === UserPersonaInCompany.WAREHOUSE_MANAGER
                    ? 'success'
                    : row.persona === UserPersonaInCompany.DEVELOPER
                      ? 'danger'
                      : 'neutral'
              }>
              {beautifyString(row.persona)}
            </Pill>
          ) : null,
      },
      {
        id: 'credential_types_sort',
        title: 'Credentials',
        width: '12%',
        minWidth: '120px',
        isUserSortable: true,
        cellContent: ({ row }) => (
          <Stack direction="row" gap="xsmall" alignItems="center">
            {row.credential_types.includes(CredentialType.API_KEY) && <Pill kind="info">API key</Pill>}
            {row.credential_types.includes(CredentialType.EXT_SHIPMENT_STATUS_API_KEY) && (
              <Pill kind="info">Ext API key</Pill>
            )}
            <Button
              onClick={() => void navigate(Routes.User.generatePath({ user_id: row.user_id }))}
              kind="subtle"
              size="xsmall">
              {row.credential_types.length > 0 ? 'Manage' : 'Add'}
            </Button>
          </Stack>
        ),
      },
      {
        id: 'credential_types',
        title: 'View',
        width: '10%',
        minWidth: '100px',
        cellContent: ({ row }) => (
          <Stack direction="row" gap="small">
            <NavLink to={Routes.User.generatePath({ user_id: row.user_id })}>User</NavLink>
            <NavLink
              to={Routes.CompanyUser.generatePath({
                company_id: company.company_id,
                user_id: row.user_id,
                slug_company_name,
              })}>
              User in Company
            </NavLink>
          </Stack>
        ),
      },
      {
        id: 'invitedByUser',
        title: 'Invited by',
        width: '10%',
        minWidth: '100px',
        cellContent: ({ row }) =>
          notNil(row.invitedByUser) ? (
            <NavLink to={Routes.User.generatePath({ user_id: row.invitedByUser.user_id })}>
              {prettyProfile(row.invitedByUser)}
            </NavLink>
          ) : null,
      },
      {
        id: 'createdAt',
        title: 'Added at',
        width: '8%',
        minWidth: '100px',
        isUserSortable: true,
        cellContent: ({ row }) => (row.createdAt ? <FormattedDate date={row.createdAt} /> : <>-</>),
      },
      {
        id: 'lastUiActivityAt_sort',
        title: (<LastActivityContextPopover />) as unknown as string,
        width: '10%',
        minWidth: '120px',
        isUserSortable: true,
        cellContent: ({ row }) =>
          notNil(row.lastUiActivityAt) ? <FormattedDate date={row.lastUiActivityAt} /> : <>-</>,
      },
    ],
    [company.company_id, slug_company_name, navigate],
  );

  const [scrollParent, setScrollParent] = useState<HTMLElement | null>(null);

  const containerRefCallback = useCallback((node: HTMLDivElement | null) => {
    if (node === null) return;
    let el: HTMLElement | null = node.parentElement;
    while (el !== null) {
      const { overflow, overflowY } = getComputedStyle(el);
      if (overflow === 'auto' || overflow === 'scroll' || overflowY === 'auto' || overflowY === 'scroll') {
        setScrollParent(el);
        return;
      }
      el = el.parentElement;
    }
  }, []);

  if ((usersInCompany ?? []).length === 0) {
    return <Text>No users</Text>;
  }

  return (
    <div ref={containerRefCallback}>
      {scrollParent !== null && (
        <InfiniteTable<UserInCompanyRow>
          columns={columns}
          data={tableData}
          sortBy={sortBy}
          onSortByChange={setSortBy}
          scrollParent={scrollParent}
          className="compact-table"
        />
      )}
    </div>
  );
};
